<template>
    <div>
        <div v-if="componentIsReady">
            <void-modal v-if="nonVoidDigitalContract && !nonVoidDigitalContract.is_final_state"
                :digital-contract="nonVoidDigitalContract"
                ref="voidModal"
                @accepted="getContractWithDigitalContract"
            ></void-modal>
            
            <before-sign-modal v-if="nonVoidDigitalContract && !nonVoidDigitalContract.is_final_state"
                :digital-contract="nonVoidDigitalContract"
                ref="beforeSignModal"
            ></before-sign-modal>

            <sign-modal v-if="contract && contract.is_digitally_signable"
                :contract="contract"
                ref="signModal"
                @accepted="getContractWithDigitalContract"
            ></sign-modal>

            <div v-if="nonVoidDigitalContract">
                <div style="display: flex; justify-content: space-between; align-items: flex-start;">
                    <div>
                        <p style="margin: 0;"><strong>ID contrat :</strong> {{ nonVoidDigitalContract.contract_id }}</p>
                        <p style="margin: 0;"><strong>Processus initié le :</strong> {{ nonVoidDigitalContract.created_at | moment-date-time }}</p>
                        <p style="margin: 0;"><strong>Statut :</strong> {{ nonVoidDigitalContract.decoded_state }}</p>
                        <p style="margin: 0;">
                            <strong>Signé par Sofradom le :</strong> 
                            <span v-if="nonVoidDigitalContract.signed_by_sofradom">{{ nonVoidDigitalContract.signed_by_sofradom_at | moment-date-time }}</span>
                            <span v-else-if="'signed' == nonVoidDigitalContract.state">date inconnue</span>
                            <span v-else class="text-danger">à signer</span>
                        </p>
                    </div>

                    <button v-if="showActionButtons && nonVoidDigitalContract && !nonVoidDigitalContract.is_final_state" 
                        class="btn btn-primary btn-sm"
                        @click="syncAndGetContractWithDigitalContract"
                    >
                        Rafraîchir <i class="fa fa-refresh" style="margin-left: 5px;"></i>
                    </button>
                </div>

                <div v-if="nonVoidDigitalContract.signatories.length > 0">
                    <table class="table" style="margin: 0;">
                        <thead>
                            <tr>
                                <th>Signataire</th>
                                <th>Coordonnées</th>
                                <th>Signature</th>
                            </tr>
                        </thead>

                        <tbody>
                            <tr v-for="(signatory, index) in nonVoidDigitalContract.signatories" :key="index">
                                <td>{{ signatory.first_name }} {{ signatory.last_name }}</td>
                                <td>{{ signatory.email }} / {{ signatory.mobile_phone }}</td>
                                <td>{{ getDisplayedSignatorySignDate(signatory.pivot.signed_at) }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div v-if="showActionButtons" class="text-right" style="margin-top: 20px;">
                    <button v-if="nonVoidDigitalContract && !nonVoidDigitalContract.is_final_state && nonVoidDigitalContract.form_link"
                        class="btn btn-primary btn-sm" 
                        @click="displayBeforeSignModal"
                    >
                        Formulaire de signature
                    </button>

                    <button v-if="nonVoidDigitalContract && !nonVoidDigitalContract.is_final_state"
                        class="btn btn-default btn-sm"
                        @click="displayVoidModal"
                    >
                        Rendre caduc
                    </button>
                </div>
            </div>

            <div v-else>
                <div class="alert alert-info">Aucun processus de signature électronique en cours</div>

                <div v-if="showActionButtons" class="text-right" style="margin-top: 20px;">
                    <button v-if="contract && contract.is_digitally_signable"
                        class="btn btn-default btn-sm"
                        @click="displaySignModal"
                    >
                        Nouveau processus
                    </button>
                </div>
            </div>
        </div>

        <component-loader v-else></component-loader>
    </div>
</template>

<script>
    import VoidModal from './VoidModal'
    import BeforeSignModal from './BeforeSignModal'
    import SignModal from './SignModal'
    import ComponentLoader from 'components/ComponentLoader'

    const DIGITAL_CONTRACT_STATE_CHANGED_EVENT = '.digital-contract.state-changed'

    export default {
        data() {
            return {
                componentIsReady: false,
                contract: null,
                subscribedDigitalContractId: null
            }
        },

        props: {
            contractId: {
                type: Number,
                required: true
            },
            customerId: {
                type: Number,
                required: true
            },
            showActionButtons: {
                type: Boolean,
                default: false
            }
        },

        components: {
            VoidModal,
            BeforeSignModal,
            SignModal,
            ComponentLoader
        },

        mounted() {
            this.onMounted()
        },

        beforeDestroy() {
            this.unsubscribeFromDigitalContractBroadcast()
        },

        computed: {
            nonVoidDigitalContract() {
                if (this.contract && this.contract.hasOwnProperty('digital_contract')) {
                    return this.contract.digital_contract
                }

                return null
            }
        },

        watch: {
            nonVoidDigitalContract: {
                immediate: true,
                handler(digitalContract) {
                    const newId = digitalContract ? digitalContract.id : null

                    if (newId === this.subscribedDigitalContractId) {
                        return
                    }

                    this.unsubscribeFromDigitalContractBroadcast()

                    if (newId !== null) {
                        this.subscribeToDigitalContractBroadcast(newId)
                    }
                }
            }
        },

        methods: {
            onMounted() {
                return this.syncAndGetContractWithDigitalContract()
            },
            subscribeToDigitalContractBroadcast(digitalContractId) {
                if (!window.Echo) {
                    return
                }

                window.Echo.private(`App.Models.DigitalContract.${digitalContractId}`)
                    .listen(DIGITAL_CONTRACT_STATE_CHANGED_EVENT, this.onDigitalContractStateChanged)

                this.subscribedDigitalContractId = digitalContractId
            },
            unsubscribeFromDigitalContractBroadcast() {
                if (!window.Echo || this.subscribedDigitalContractId === null) {
                    return
                }

                window.Echo.private(`App.Models.DigitalContract.${this.subscribedDigitalContractId}`)
                    .stopListening(DIGITAL_CONTRACT_STATE_CHANGED_EVENT)

                this.subscribedDigitalContractId = null
            },
            onDigitalContractStateChanged() {
                this.getContractWithDigitalContract()
            },
            syncDigitalContracts() {
                return this.axios.get(`customers/${this.customerId}/contracts/${this.contractId}/sync_digital_contracts`)
            },
            getContractWithDigitalContract() {
                this.componentIsReady = false
                
                return this.axios
                    .get(`customers/${this.customerId}/contracts/${this.contractId}?with=digital_contract.signatories`)
                    .then(response => {
                        this.contract = response.data.contract
                        this.componentIsReady = true
                    })
            },
            syncAndGetContractWithDigitalContract() {
                return this.syncDigitalContracts()
                    .then(response => {
                        this.getContractWithDigitalContract()
                    })
            },
            getDisplayedSignatorySignDate(signDate) {
                if (signDate) {
                    return this.$options.filters['moment-date-time'](signDate)
                }

                return 'En attente'
            },
            displayVoidModal() {
                this.$refs.voidModal.displayModal()
            },
            displayBeforeSignModal() {
                this.$refs.beforeSignModal.displayModal()
            },
            displaySignModal() {
                this.$refs.signModal.displayModal()
            }
        }
    }
</script>


// WEBPACK FOOTER //
// client/components/Customer/InformationsContent/Contracts/DigitalSignature/NonVoidContract.vue